/* eslint-disable jira/filename-case */

import memoize from 'lodash/memoize';
import { createExpirableStorageProvider } from '../../createExpirableStorageProvider.tsx';
import { createStorageProvider } from '../../createStorageProvider.tsx';
import {
	type ExpirableWebStorageProvider,
	type WebStorageProvider,
	WebStorageType,
} from '../../types.tsx';
import { makeLocalExpirableStore, makeLocalStore } from './utils.tsx';

/**
 * Creates a memoized localStorage-backed storage provider for persisting data across browser sessions.
 * Not suitable for expirable data - use createLocalExpirableStorageProvider instead.
 *
 * @agent-skills: browser-storage, state-management
 */
export const createLocalStorageProvider: (appPrefix?: string | undefined) => WebStorageProvider =
	memoize(
		(appPrefix?: string | undefined): WebStorageProvider =>
			createStorageProvider(WebStorageType.LocalStorage, appPrefix, makeLocalStore()),
	);

/**
 * Creates a memoized localStorage-backed storage provider with automatic expiration support.
 * Data persists across browser sessions until explicitly expired. Not suitable for non-expirable data.
 *
 * @agent-skills: browser-storage, state-management
 */
export const createLocalExpirableStorageProvider: (
	appPrefix: string,
) => ExpirableWebStorageProvider = memoize((appPrefix: string): ExpirableWebStorageProvider => {
	return createExpirableStorageProvider(
		WebStorageType.LocalStorage,
		appPrefix,
		makeLocalExpirableStore(),
	);
});
